import Vue from 'vue'
import { api, ApiError, AUTH_INVALID_EVENT, clearToken, getToken, setToken } from '@/api/client'
import { inviteShareIdInAddress, getInviteShare, resolveLineCode } from '@/utils/channel'

const USER_KEY = 'kp_user'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function readUser() {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export const session = Vue.observable({
  token: getToken(),
  user: readUser(),
})

function saveUser(user) {
  session.user = user
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_KEY)
  } catch (e) {
    /* ignore */
  }
}

function saveToken(token) {
  session.token = token
  setToken(token)
}

function clearSession() {
  session.token = ''
  clearToken()
  saveUser(null)
}

if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_INVALID_EVENT, clearSession)
}

export function isLoggedIn() {
  return !!session.token
}

/** 注册带的会员邀请码：地址栏 shareId 优先，再用本次会话记住的 */
export function registerShareId() {
  const fromAddress = inviteShareIdInAddress()
  if (fromAddress) return fromAddress
  const saved = getInviteShare()
  return String((saved && saved.shareId) || '').trim().toUpperCase()
}

export function loadCaptcha() {
  return api.captcha()
}

export async function refreshMe() {
  if (!session.token) {
    saveUser(null)
    return null
  }
  try {
    const info = await api.infoById()
    saveUser(info)
    return info
  } catch (e) {
    if (e && (e.code === 401 || e.code === 403)) clearSession()
    throw e
  }
}

export async function loginByEmail(account, password, captchaId, captchaCode) {
  if (!String(account || '').trim()) throw ApiError('请输入邮箱/账号')
  if (!password) throw ApiError('请输入密码')
  if (!String(captchaCode || '').trim()) throw ApiError('请输入图形验证码')
  const token = await api.emailLogin(account.trim(), password, captchaId, captchaCode.trim())
  if (!token || typeof token !== 'string') throw ApiError('登录失败：未返回 Token')
  saveToken(token)
  await refreshMe()
}

export async function sendRegisterCode(email, captchaId, captchaCode) {
  if (!EMAIL_RE.test(String(email || '').trim())) throw ApiError('请输入正确邮箱')
  if (!String(captchaCode || '').trim()) throw ApiError('请输入图形验证码')
  await api.emailRegisterSendCode(email.trim(), captchaId, captchaCode.trim())
}

export async function registerByEmail(payload) {
  const email = String(payload.email || '').trim()
  if (!EMAIL_RE.test(email)) throw ApiError('请输入正确邮箱')
  if (String(payload.password || '').length < 6) throw ApiError('密码至少 6 位')
  if (!String(payload.code || '').trim()) throw ApiError('请输入邮箱验证码')
  if (!String(payload.captchaCode || '').trim()) throw ApiError('请输入图形验证码')

  const token = await api.emailRegister({
    email,
    password: payload.password,
    code: payload.code.trim(),
    captchaId: payload.captchaId,
    captchaCode: payload.captchaCode.trim(),
    shareId: registerShareId() || undefined,
    agentCode: resolveLineCode() || undefined,
  })
  if (!token || typeof token !== 'string') throw ApiError('注册失败：未返回 Token')
  saveToken(token)
  await refreshMe()
}

export async function logout() {
  try {
    if (session.token) await api.signOut()
  } catch (e) {
    /* ignore */
  }
  clearSession()
}

export async function sendBindCode(email) {
  if (!EMAIL_RE.test(String(email || '').trim())) throw ApiError('请输入正确邮箱')
  await api.sendEmailCode(email.trim())
}

export async function bindEmail(email, code) {
  if (!EMAIL_RE.test(String(email || '').trim())) throw ApiError('请输入正确邮箱')
  if (!String(code || '').trim()) throw ApiError('请输入验证码')
  await api.verifyEmailCode(email.trim(), code.trim())
  await refreshMe()
}

export async function changePassword(password) {
  if (String(password || '').length < 6) throw ApiError('新密码至少 6 位')
  await api.changePassword(password)
}

export async function cancelAccount() {
  await api.cancelAccount()
  clearSession()
}

export function displayAccount(user) {
  if (!user) return ''
  return String(user.email || user.account || user.username || user.id || '')
}

export function displayPlan(user) {
  if (!user) return '未登录'
  if (user.planName) return String(user.planName)
  const kind = String(user.memberKind || '').toLowerCase()
  if (kind === 'trial') return '体验用户'
  if (kind === 'expired') return '已过期'
  if (kind === 'flow') return '流量会员'
  if (kind === 'time') return '时长会员'
  const n = Number(user.vipType)
  if (n === 5) return user.vipActive ? '流量会员' : '体验 / 未开通'
  if (n >= 1 && n <= 4) {
    const names = ['', '周卡会员', '月卡会员', '季卡会员', '年卡会员']
    return user.vipActive ? (names[n] || '时长会员') : '体验 / 未开通'
  }
  return user.vipActive ? '会员' : '体验用户'
}

export function displayExpire(user) {
  if (!user) return '—'
  return String(user.vipExpiredTime || user.expireAt || user.expireTime || '—')
}

function flowLabel(mb) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`
  return `${mb} MB`
}

export function displayTraffic(user) {
  if (!user) return '—'
  const left = Number(user.remainFlow != null ? user.remainFlow : user.trafficLeft)
  if (user.remainFlow == null && user.trafficLeft == null) return '—'
  if (!Number.isFinite(left)) return '—'
  const total = Number(user.totalFlow)
  if (Number.isFinite(total) && total > 0) return `${flowLabel(left)} / ${flowLabel(total)}`
  return flowLabel(left)
}

export function displayBalance(user) {
  if (!user || user.balance == null || user.balance === '') return '¥0'
  const text = String(user.balance)
  return text.charAt(0) === '¥' ? text : `¥${text}`
}

export function isVip(user) {
  return !!(user && user.vipActive)
}

/** 拉节点 / 订阅时用的轨道：免费领取走 free，时长会员走 time，流量会员走 flow */
export function resolveAccess(user) {
  if (!user) return 'free'
  if (user.canUseVipLine === false || user.vipActive === false) return 'free'

  const kind = String(user.memberKind || '').toLowerCase()
  if (kind === 'flow') return 'flow'
  if (kind === 'time') return 'time'
  if (kind === 'trial' || kind === 'expired') return 'free'

  const track = String(user.track || user.packageTrack || '').toLowerCase()
  if (track === 'flow') return 'flow'
  if (track === 'time') return 'time'

  const n = Number(user.vipType)
  if (Number.isFinite(n)) {
    if (n === 5) return 'flow'
    if (n >= 1 && n <= 4) return 'time'
    return 'free'
  }

  if (user.canUseVipLine) return 'time'
  return 'free'
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Number(value)))
}

/** 会员进度条，优先用服务端算好的百分比 */
export function memberProgress(user) {
  if (!user) return null
  const track = String(user.track || user.packageTrack || '').toLowerCase()
  if (track === 'flow' || Number(user.vipType) === 5) {
    if (user.flowProgressPercent != null && Number.isFinite(Number(user.flowProgressPercent))) {
      return { label: '流量剩余', percent: clampPercent(user.flowProgressPercent), detail: displayTraffic(user) }
    }
    const left = Number(user.remainFlow)
    const total = Number(user.totalFlow)
    if (Number.isFinite(left) && Number.isFinite(total) && total > 0) {
      return { label: '流量剩余', percent: clampPercent((left / total) * 100), detail: displayTraffic(user) }
    }
    return null
  }
  if (user.memberProgressPercent != null && Number.isFinite(Number(user.memberProgressPercent))) {
    const days = user.remainDays
    return {
      label: '会员剩余',
      percent: clampPercent(user.memberProgressPercent),
      detail: days != null ? `剩余约 ${days} 天 · ${displayExpire(user)}` : displayExpire(user),
    }
  }
  return null
}
